/**
 * Settings sub-tab (v2 — DNKADM-0056 U2) — the membership-settings form for
 * `GET/PUT /v2/admin/membership-settings` (§5.10).
 *
 * Loads the single-record settings through `useResource` (loading / ready / error)
 * and saves through `putMembershipSettings`. The form (`MembershipSettingsCard`) is
 * remounted on each successful (re)load so its inputs reseed from the freshly loaded
 * view. Client-side range validation mirrors the server (`graceDays` integer ≥ 0,
 * `refundRoundingIncrement` > 0); a successful save raises a success toast and
 * `refetch`es (refetch-after-write). A validation `400` (a bad range or an unexpected
 * rounding mode) renders INLINE; an upstream/`502` raises a non-blocking error toast.
 */
import { useCallback } from 'react';
import { Skeleton } from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { getMembershipSettings } from '@/api/membershipSettings';
import { MembershipSettingsCard } from './MembershipSettingsCard';
import { ScreenError } from '@/components/ScreenError';

export function SettingsTab() {
  const showToast = useToast();
  const settings = useResource(useCallback(() => getMembershipSettings(), []));

  if (settings.status === 'loading') {
    return (
      <Skeleton
        variant="rounded"
        height={360}
        role="status"
        aria-label="Loading membership settings"
        sx={{ borderRadius: 3, maxWidth: 560 }}
      />
    );
  }

  if (settings.status === 'error' || !settings.data) {
    return (
      <ScreenError
        error={settings.error}
        what="the membership settings"
        onRetry={settings.refetch}
        sx={{ maxWidth: 560 }}
      />
    );
  }

  return (
    <MembershipSettingsCard
      // Remount on each successful (re)load so the inputs reseed from the freshly
      // loaded view.
      key={`${String(settings.data.creditsAdditive)}|${settings.data.graceDays}|${settings.data.refundRoundingIncrement}|${settings.data.refundRoundingMode}|${settings.data.maxReschedules}`}
      data={settings.data}
      refetch={settings.refetch}
      showToast={showToast}
    />
  );
}
